import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MessageSquarePlus, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { getErrorMessage } from '@/utils/helpers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CATEGORIES = [
  { id: 'bug', label: 'Something broke' },
  { id: 'idea', label: 'Idea' },
  { id: 'other', label: 'Other' },
];

// Beta feedback form; token is optional so it works on public pages too
export const FeedbackDialog = ({ portal = 'landing', token, askEmail = false, triggerClassName = '', label = 'Feedback' }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ category: 'bug', message: '', rating: null, email: '' });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const payload = { ...form, portal, page: window.location.pathname, email: form.email || null };
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const { data } = await axios.post(`${BACKEND_URL}/api/feedback`, payload, { headers });
      toast.success(data.message);
      setForm({ category: 'bug', message: '', rating: null, email: '' });
      setOpen(false);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not send feedback'));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid={`feedback-open-${portal}`}
        className={`inline-flex items-center gap-2 text-sm font-medium transition-colors ${triggerClassName}`}>
        <MessageSquarePlus className="w-4 h-4" /> <span className={portal === 'user' ? 'hidden sm:inline' : ''}>{label}</span>
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md bg-white text-stone-900" data-testid="feedback-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">Laksh is in beta - tell us what you think</DialogTitle>
            <DialogDescription>Report a problem or suggest an improvement. We read every message.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="flex gap-2">
              {CATEGORIES.map((c) => (
                <button key={c.id} type="button" onClick={() => setForm({ ...form, category: c.id })}
                  data-testid={`feedback-category-${c.id}`}
                  className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${form.category === c.id ? 'bg-[#1D9E75] text-white border-[#1D9E75]' : 'border-stone-300 text-stone-600 hover:border-[#1D9E75]'}`}>
                  {c.label}
                </button>
              ))}
            </div>
            <Textarea required minLength={3} rows={4} value={form.message} placeholder="What happened, or what would make Laksh better?"
              onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="feedback-message"
              className="bg-white border-stone-300 text-stone-900" />
            {askEmail && (
              <Input type="email" value={form.email} placeholder="Your email (optional, so we can reply)"
                onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="feedback-email"
                className="bg-white border-stone-300 text-stone-900" />
            )}
            <div className="flex items-center gap-1" aria-label="Rating">
              <span className="text-sm text-stone-500 mr-2">How's it going?</span>
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button" onClick={() => setForm({ ...form, rating: n })} data-testid={`feedback-rating-${n}`}>
                  <Star className={`w-5 h-5 ${form.rating >= n ? 'fill-[#EF9F27] text-[#EF9F27]' : 'text-stone-300'}`} />
                </button>
              ))}
            </div>
            <Button type="submit" disabled={sending || form.message.trim().length < 3} data-testid="feedback-submit"
              className="w-full bg-[#1D9E75] hover:bg-[#178a65] text-white">
              {sending ? 'Sending...' : 'Send feedback'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FeedbackDialog;
